import { debounce } from "lodash";
import { useEffect, useRef, useState } from 'react';
import { IAdresseSearchFeature, IAdresseSearchFeatureProps } from '../../context/model';
import { searchDqeAddress } from '../../services/CustomerApiService';
import { accessJsonAttr, cleanUpSpecialChars } from '../../utils/utils';
import { AddressFormAutoCompleteProps } from './MgpAddressFormAutoComplete.types';
import useAppState from '../../context/AppContext';

function useOutsideAlerter(ref: any,  outsideAction: () => void) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        outsideAction();
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);
}


export default function MgpAddressFormAutoComplete({id, label, stopSearch, register, required, labelFontWeight, errors, onDataSelect, formKey, resultType, clearError, setInputValue, ...props }: Readonly<AddressFormAutoCompleteProps>)  {
 
  const {
    state: {jwtToken, },
  } = useAppState(); 
  const searchAdresse = async (
    addresse: string,
    setResults: (value: IAdresseSearchFeature[]) => void,
    setIsLoading: (value: boolean) => void
  ) => {
    try {
        const token = jwtToken!.accessToken;
        const payload = await searchDqeAddress(token, addresse)
        setIsLoading(false);
        
        let results = [] as IAdresseSearchFeature[];
        for(let i = 0;i < 6; i++) {
          let record = payload.data[i];
          if(record) {
            let featureProps = {
              name: record.streetNumber + " " + record.streetName,
              housenumber: record.streetNumber,
              street: record.streetName,
              city: record.city,
              postcode: record.postalCode
            } as IAdresseSearchFeatureProps;
            results = [...results, {properties: featureProps} as IAdresseSearchFeature];
            results = results.filter((value, index, self) =>
            index === self.findIndex((t) => (
              t.properties.name === value.properties.name 
              && t.properties.city === value.properties.city 
              && t.properties.postcode === value.properties.postcode
            ))
          )
          }
        }
        setResults(results);
    } catch(e) {

    }
  };
  const DEBOUNCE = 250;
  const debouncedSearch = debounce(searchAdresse, DEBOUNCE);

    const [results, setResults] = useState<IAdresseSearchFeature[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const selectBoxRef = useRef(null);
    const [value, setValue] = useState(props.value);
    
    let labelClassName = "text fw-" + (labelFontWeight ?? "medium");
    
    let inputClassName = "form-control";
    let fieldError;
    if(errors) {
        fieldError = accessJsonAttr(errors, id);
        if(fieldError) {
            inputClassName += " form-error";
            labelClassName += " form-error";
        }
    }

    useEffect(()=>{
      // manually remove invalid input error after filling the input
      typeof(value) === "string" 
      && value 
      && value.length !== 0 
      &&  clearError?.(id as any)
    }, [value])

    const onSearch = async (v: string) => {
      if(stopSearch) {
        setResults([]);
        setIsLoading(false);
        return;
      }  
      const searchAction = debouncedSearch;
        if (!v || v.trim().length < 4) {
          // when the user clear the field we don't want to perform a search, 
          // we need to clear the state and do nothing else
          debouncedSearch.cancel();
          setResults([]);
          setIsLoading(false);
        } else {
          setIsLoading(true);
          searchAction(v, setResults, setIsLoading);
        }
      };

      const onResultSelect = (result: IAdresseSearchFeature) => {
        setResults([]);
        setIsLoading(false);
        
        // update value from autocomplete input
        let res = result.properties.name
        if( resultType === 'full') {
          const extendedText = ', ' +  result.properties.postcode + " " +  result.properties.city
          res += extendedText 
        }
        setValue(res) ;
        setInputValue?.(formKey ?? '', res)  
        onDataSelect && onDataSelect(result);
      }
      const closeDropDown = () => {
        setResults([]);
        setIsLoading(false);
      }

      useOutsideAlerter(selectBoxRef, closeDropDown);

      const placeholderText = stopSearch ? "": "Recherche d'adresse...";
    
        return (
            <>
                {label && <label className={labelClassName} htmlFor={id}>
                  {label}{required ? <span className='fushia'> (*)</span>: ''}
                </label>}
                <div>
         
                  <input 
                      autoComplete="off" 
                      spellCheck={false} 
                      placeholder={placeholderText} 
                      className={inputClassName} 
                      id={id} 
                      {...register(id)} 
                      value={value} 
                      onChange={(e) => {
                          setValue(cleanUpSpecialChars(e.target.value).toUpperCase());
                          onSearch(e.target.value)
                      }} 
                      onFocus={(e) => onSearch(e.target.value)} 
                      {...props} 
                  />

                
                  {fieldError && (<p className="error">{fieldError.message}</p>)}

                  {results && results.length > 0 && (
                      <ul ref={selectBoxRef}>
                      {results.map((result, index) =>
                        <li key={index} onClick={() => {onResultSelect(result)}}>
                          <p className="text">{result.properties.name}</p>
                          <p className="text fw-medium">{result.properties.postcode} {result.properties.city}</p>
                        </li>
                      )}
                    </ul>  
                  )}
                </div>
                {/* {isLoading && <LoaderModal hasMessage={false}/>} */}
            </>
        );
    }